/**
 * WordPress dependencies
 */
import { store, getContext } from '@wordpress/interactivity';

/**
 * Internal dependencies
 */
import { getUserLocation, fetchWeatherData } from './utils/weather';
import { applyWeatherClass } from './utils/weatherConditionMapping';

// Initialize the store with minimal state
const { actions } = store('bigbite-block-weather', {
  actions: {
    async refreshWeather() {
      const context = getContext();

      try {
        // Update context to show loading
        context.loading = true;
        context.error = false;
        context.hideContent = true; // Hide content during loading

        // Determine location using context settings
        const locationData = await determineLocationForView(context.locationSettings);
        const { latitude, longitude, locationName } = locationData;

        // Fetch weather data via REST API
        const weatherData = await fetchWeatherData(
          latitude,
          longitude,
          locationName,
          context.locationSettings.units,
          context.locationSettings.cacheDuration,
        );

        // Update context with weather data
        context.loading = false;
        context.error = false;
        context.hideContent = false; // Show content when loaded successfully
        context.weather = weatherData;
        context.location = weatherData.location;

        // Apply weather-based CSS class for dynamic styling
        const blockElement = document.getElementById(context.blockId);
        if (weatherData.conditionId && blockElement) {
          applyWeatherClass(blockElement, weatherData.conditionId);
        }
      } catch (error) {
        // Handle errors in context
        context.loading = false;
        context.error = true;
        context.hideContent = true; // Hide content when there's an error
        context.errorMessage = error.message || 'Failed to load weather data. Please try again.';
      }
    },
  },

  callbacks: {
    async initWeather() {
      // Simply fetch weather data for this specific instance
      await actions.refreshWeather();
    },
  },
});

/**
 * Handle manual location source
 *
 * @param {string} manualLocation The manual location
 * @return {Object} Location data object
 */
const handleManualLocation = (manualLocation) => {
  if (manualLocation && manualLocation.trim()) {
    return { locationName: manualLocation };
  }
  throw new Error('Please enter a location in the block settings.');
};

/**
 * Handle user location source
 *
 * @return {Promise<Object>} Location data object with coordinates
 */
const handleUserLocation = async () => {
  try {
    const position = await getUserLocation();
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  } catch (error) {
    throw new Error(
      'Could not determine your location. Please allow location access or enter a location manually.',
    );
  }
};

/**
 * Determine location based on settings
 *
 * @param {Object} locationSettings The location settings
 * @return {Promise<Object>} Object with location data
 */
const determineLocationForView = async (locationSettings) => {
  const { source, manualLocation, taxonomyLocation } = locationSettings;

  // Handle specific source types
  if (source === 'manual') {
    return handleManualLocation(manualLocation);
  }

  if (source === 'taxonomy') {
    if (taxonomyLocation && taxonomyLocation.trim()) {
      return { locationName: taxonomyLocation };
    }
    throw new Error('No taxonomy location available. Please enter a location manually.');
  }

  if (source === 'user') {
    return handleUserLocation();
  }

  throw new Error('No location configured. Please check your block settings.');
};
